import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { SxProps } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Fragment, type ReactNode, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import type { DocDetail } from "@xop/dashboard-shared";

import { useDocumentCapabilities } from "../../auth/permissions.js";
import { saveBlob } from "../../lib/download.js";
import { ShareMenu } from "../../components/share/ShareMenu.js";
import { ShareToChatDialog } from "../../components/share/ShareToChatDialog.js";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { fetchDocFileBytes, fetchDocSourceFileBytes, getDoc } from "./api.js";
import { resolveDocDownload } from "./docDownload.js";
import { buildDocShareItems } from "./docShareItems.js";
import { documentShareSubject } from "./shareSubject.js";
import { buildDocPdf } from "./docPdf.js";
import { type DocReaderMode, selectDocReaderMode } from "./docReader.js";
import { FaqReader } from "./FaqReader.js";
import { Markdown } from "./markdown.js";
import { PdfInlinePreview } from "./PdfInlinePreview.js";
import { VideoReader } from "./VideoReader.js";
import { formatBytes } from "./utils.js";

/** Single-document reader: a compact header (identity + actions) over a
 * TYPE-AWARE body (`DocReaderBody` / `selectDocReaderMode`) — FAQ → Q&A,
 * video → player, everything else → the inline PDF reader (original /
 * uploaded / body-built PDF), with images & audio inline. It never dumps a
 * raw markdown body or web scrape as plain text. See `docReader.ts` +
 * `docs/documentation.md` → "Reader is type-aware". */
export function LibraryDocPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  // "Edit document" shows for a full editor on any doc, or for a create-tier
  // author on a doc they wrote (applied to the loaded doc below).
  const { canModify } = useDocumentCapabilities();
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [chatShareOpen, setChatShareOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["docs", "single", slug],
    queryFn: () => getDoc(slug ?? ""),
    enabled: !!slug,
  });

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14, color: "error.main" }}>
            Could not load document:{" "}
            {error instanceof Error ? error.message : "not found"}
          </Typography>
          <BackButton to="/documentation" label="Back to documentation" sx={{ mt: 1.5 }} />
        </CardContent>
      </Card>
    );
  }

  const d = data.data;
  const file = d.files[0];
  // Faithful original PDF (whitepaper/datasheet/press-release) parked in its own
  // association — coexists with the markdown body. When present it's both the
  // inline-preview source AND what the single Download button saves.
  const sourceFile = d.source_file ?? null;

  // ONE "Download" action for the whole reader:
  //   • an UPLOADED doc → the ORIGINAL file (the faithful `source_file` PDF, or
  //     the uploaded attachment of whatever type) — exactly what the user put in;
  //   • a MANUALLY-authored doc (a markdown body with no upload) → a PDF built
  //     client-side from the body (`buildDocPdf`), so a hand-written article
  //     still downloads as a real document, never a plain-text dump.
  // Both the original-file bytes and the built PDF go out through `saveBlob`
  // (the native Save-As dialog on the desktop app) and are read from the
  // authenticated SAME-ORIGIN passthrough — so it behaves identically on web +
  // the Tauri shells, with no cross-origin fetch.
  async function downloadDoc(): Promise<void> {
    setDownloadBusy(true);
    setDownloadError(null);
    try {
      const { filename, blob } = await resolveDocDownload(d);
      await saveBlob(filename, blob);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Could not download this document",
      );
    } finally {
      setDownloadBusy(false);
    }
  }
  const updated = formatDateMaybe(d.updated_at);

  // Subtle dot-separated meta line under the title — folds the old bulky
  // 4-column <dl> grid into one compact row. The filename is its OWN node
  // (small + secondary) rather than a second bold heading under the title,
  // which read as "the title shown twice". (Reported 2026-06-06.)
  const meta: ReactNode[] = [];
  if (file) {
    meta.push(
      <Box component="span" key="filename" sx={{ fontWeight: 500, color: "text.secondary" }}>
        {file.filename}
      </Box>,
    );
    meta.push(<span key="size">{formatBytes(file.byte_size)}</span>);
  }
  if (sourceFile) {
    meta.push(
      <Box component="span" key="original" sx={{ color: "text.secondary" }}>
        Original PDF · {formatBytes(sourceFile.byte_size)}
      </Box>,
    );
  }
  if (updated) meta.push(<span key="updated">Updated {updated}</span>);
  if (d.author) meta.push(<span key="author">{d.author.name}</span>);
  if (d.views_count > 0) meta.push(<span key="views">{d.views_count.toLocaleString()} views</span>);
  if (d.versions_count > 1) meta.push(<span key="versions">{d.versions_count} versions</span>);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {downloadError && (
        <Typography sx={{ fontSize: 12, color: "error.main" }} role="alert">
          {downloadError}
        </Typography>
      )}

      <Card>
        <CardContent sx={{ display: "grid", gap: 2 }}>
          {/* Compact one-row header: identity (back-link, title, taxonomy
            * chips, meta) on the LEFT; the single Download action (+ Edit for
            * authors) on the RIGHT. (Redesigned 2026-06-06; collapsed to one
            * Download 2026-06-27.) */}
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0, display: "grid", gap: 1 }}>
              <BackButton to="/documentation" label="Documentation" sx={{ justifySelf: "start" }} />
              <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                {d.title}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                {d.category && <DocChip tone="brand">{d.category.name}</DocChip>}
                {d.default_release && <DocChip tone="neutral">v{d.default_release.version}</DocChip>}
                {d.featured && <DocChip tone="warn">Featured</DocChip>}
                {d.status === "draft" && <DocChip tone="neutral">Draft</DocChip>}
                {d.status === "in_review" && <DocChip tone="warn">In review</DocChip>}
                {d.status === "published" && <DocChip tone="good">Published</DocChip>}
                {d.current_version && (
                  <DocChip tone="neutral">v{d.current_version.version_number}</DocChip>
                )}
              </Box>
              {meta.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, fontSize: 12, color: "text.disabled" }}>
                  {meta.map((node, idx) => (
                    <Fragment key={idx}>
                      {idx > 0 && (
                        <Box component="span" aria-hidden sx={{ color: "text.disabled" }}>·</Box>
                      )}
                      {node}
                    </Fragment>
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", flexShrink: 0, flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              {/* Share ▾ — the canonical trio (email / chat / copy link), shown
                  to anyone who can view the doc. See `buildDocShareItems`. */}
              <ShareMenu
                items={buildDocShareItems({ doc: d, onShareToChat: () => setChatShareOpen(true) })}
              />
              {/* ONE Download button (the reader's only file action). An
                  uploaded doc downloads its ORIGINAL file; a manually-authored
                  doc downloads a PDF built from the body — see `downloadDoc`. */}
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => void downloadDoc()}
                disabled={downloadBusy}
                sx={{ bgcolor: "action.hover" }}
              >
                {downloadBusy ? "Preparing…" : "Download"}
              </Button>
              {canModify(d) && (
                <Button
                  component={RouterLink}
                  to={`/documentation/manage/${d.slug}/edit`}
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{ bgcolor: "action.hover" }}
                >
                  Edit document
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <DocReaderBody detail={d} />
          </Box>
        </CardContent>
      </Card>

      <ShareToChatDialog
        open={chatShareOpen}
        onClose={() => setChatShareOpen(false)}
        subject={documentShareSubject(d)}
      />
    </Box>
  );
}

/** `YYYY-MM-DD`/ISO → locale date, or null for empty/invalid input (the
 * serializer sends "" for never-updated docs → "Invalid Date" otherwise). */
function formatDateMaybe(value: string | null | undefined): string | null {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt.toLocaleDateString();
}

/** Status / taxonomy pill in the doc header. Tones map onto theme
 * palette roles (was the legacy tone-based Badge). */
function DocChip({ tone, children }: { tone: "neutral" | "good" | "bad" | "warn" | "brand"; children: React.ReactNode }): JSX.Element {
  const toneSx: SxProps<Theme> =
    tone === "good"
      ? { bgcolor: (t) => alpha(t.palette.success.main, 0.15), color: "success.main" }
      : tone === "bad"
        ? { bgcolor: (t) => alpha(t.palette.error.main, 0.15), color: "error.main" }
        : tone === "warn"
          ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.main" }
          : tone === "brand"
            ? { bgcolor: "action.selected", color: "primary.main" }
            : { bgcolor: "surfaceSoft", color: "text.secondary" };
  return (
    <Chip
      label={children}
      size="small"
      sx={[{ height: 22, borderRadius: 0.75, fontSize: 12, "& .MuiChip-label": { px: 1 } }, toneSx]}
    />
  );
}

/**
 * Type-aware reader body. Picks the renderer from `selectDocReaderMode`
 * (pure, unit-tested in `docReader.test.ts`) so the reader NEVER dumps a raw
 * markdown body / web scrape as plain text:
 *
 *   • video      → `<VideoReader>` (player — upload `<video>` or hosted iframe)
 *   • faq        → `<FaqReader>` (one Q&A per item)
 *   • pdf        → `<PdfInlinePreview>` (pdf.js canvas) fed by the `source_file`
 *                  ORIGINAL PDF, the uploaded PDF, or a PDF built client-side
 *                  from the markdown body — see `pdfReaderProps`
 *   • image/av   → native `<img>` / `<video>` / `<audio>` (`FilePreview`)
 *   • file-card  → "no inline preview" card for un-renderable binaries
 *   • empty      → empty-state note
 */
function DocReaderBody({ detail }: { detail: DocDetail }): JSX.Element {
  const mode = selectDocReaderMode(detail);
  switch (mode.kind) {
    case "video":
      return <VideoReader detail={detail} />;
    case "faq":
      return <FaqReader detail={detail} />;
    case "pdf": {
      const props = pdfReaderProps(detail, mode);
      return (
        <PdfInlinePreview
          docId={detail.id}
          filename={props.filename}
          loader={props.loader}
          cacheKey={props.cacheKey}
        />
      );
    }
    case "text":
      return <TextFileReader docId={detail.id} markdown={mode.markdown} />;
    case "image":
    case "av":
    case "file-card":
      return <FilePreview mode={mode} />;
    case "empty":
    default:
      return (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          This document has no content yet.
        </Typography>
      );
  }
}

/** Byte source + cache key + filename for the three PDF reader modes:
 *   • `source` → the faithful ORIGINAL PDF via the `/source-file` passthrough
 *   • `file`   → the uploaded PDF via the `/file` passthrough
 *   • `built`  → a PDF generated client-side from the markdown body
 * All same-origin / in-memory bytes → desktop-safe (no cross-origin iframe,
 * no native PDF plugin). */
function pdfReaderProps(
  detail: DocDetail,
  mode: Extract<DocReaderMode, { kind: "pdf" }>,
): {
  loader: () => Promise<{ bytes: ArrayBuffer; contentType: string | null }>;
  cacheKey: readonly unknown[];
  filename: string;
} {
  const fallbackName = `${detail.slug}.pdf`;
  if (mode.via === "source") {
    return {
      loader: () => fetchDocSourceFileBytes(detail.id),
      cacheKey: ["docs", "pdf", "source", detail.id],
      filename: mode.file?.filename ?? fallbackName,
    };
  }
  if (mode.via === "file") {
    return {
      loader: () => fetchDocFileBytes(detail.id),
      cacheKey: ["docs", "pdf", "file", detail.id],
      filename: mode.file?.filename ?? fallbackName,
    };
  }
  // built — render the markdown body as a clean vector PDF (never plain text)
  return {
    loader: async () => {
      const { blob } = await buildDocPdf(detail, { fetchFileBytes: fetchDocFileBytes });
      return { bytes: await blob.arrayBuffer(), contentType: "application/pdf" };
    },
    cacheKey: ["docs", "pdf", "built", detail.id],
    filename: fallbackName,
  };
}

/**
 * Inline reader for an uploaded **text** file. Markdown (`.md`) renders through
 * the same `<Markdown>` renderer the authored body uses (so an uploaded `.md`
 * reads exactly like a hand-written article); any other plain-text file
 * (`.txt`, `.csv`, `.json`, `.log`, …) renders as preformatted text. The bytes
 * arrive through the authenticated **same-origin** `/file` passthrough
 * (`fetchDocFileBytes`) and are decoded as UTF-8 — nothing cross-origin is
 * fetched, so it behaves identically on web + the Tauri desktop shells. On any
 * failure it degrades to a "use Download" card, never a blank.
 */
function TextFileReader({ docId, markdown }: { docId: number; markdown: boolean }): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["docs", "text", docId],
    queryFn: async () => {
      const { bytes } = await fetchDocFileBytes(docId);
      return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
    },
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (isError || data == null) {
    return (
      <Box
        data-testid="doc-text-error"
        sx={{ borderRadius: 1, border: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3), p: 2, fontSize: 14, color: "text.disabled" }}
      >
        Couldn’t load this file inline. Use{" "}
        <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>Download</Box>{" "}
        above to save the original.
      </Box>
    );
  }

  if (markdown) {
    // Cap line length for readability, exactly like the authored-body reader.
    return (
      <Box data-testid="doc-text-markdown" sx={{ maxWidth: "76ch" }}>
        <Markdown source={data} />
      </Box>
    );
  }

  return (
    <Box
      component="pre"
      data-testid="doc-text-plain"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 13,
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowX: "auto",
      }}
    >
      {data}
    </Box>
  );
}

/**
 * Native inline preview for an uploaded **image / audio / video** file, or a
 * "no inline preview" card for an un-renderable binary (Office / zip / …).
 * PDFs never reach here — they go through `<PdfInlinePreview>` (pdf.js canvas)
 * upstream. The file's identity + the single Download action live in the
 * page's compact header. `url` resolves to `preview_url ?? download_url`; an
 * empty `url` means the upload never finished — surface that, not a blank.
 */
function FilePreview({
  mode,
}: {
  mode: Extract<DocReaderMode, { kind: "image" | "av" | "file-card" }>;
}): JSX.Element {
  const file = mode.file;
  const url = file.preview_url ?? file.download_url ?? "";

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {!url && mode.kind !== "file-card" && (
        <Typography sx={{ fontSize: 14, color: "error.main" }}>
          The file URL is missing — the upload may not have finished. Check
          the support-center logs.
        </Typography>
      )}

      {url && mode.kind === "image" && (
        <Box sx={{ overflow: "hidden", borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "background.paper", boxShadow: (t) => t.shadows[1] }}>
          {/* alt intentionally mirrors the filename for this document preview. */}
          <Box component="img" src={url} alt={file.filename} sx={{ display: "block", maxHeight: "70vh", width: "100%", objectFit: "contain" }} />
        </Box>
      )}

      {url && mode.kind === "av" && mode.media === "video" && (
        <Box
          component="video"
          controls
          preload="metadata"
          sx={{ width: "100%", borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "#000" }}
        >
          <source src={url} type={file.content_type ?? undefined} />
          Your browser does not support inline video playback.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      )}

      {url && mode.kind === "av" && mode.media === "audio" && (
        <Box component="audio" controls preload="metadata" sx={{ width: "100%" }}>
          <source src={url} type={file.content_type ?? undefined} />
          Your browser does not support inline audio playback.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      )}

      {mode.kind === "file-card" && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
            p: 2,
            fontSize: 14,
            color: "text.disabled",
          }}
        >
          This file type has no inline preview. Use{" "}
          <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>Download</Box>{" "}
          above to save the original file locally.
        </Box>
      )}
    </Box>
  );
}
